<script>
import useConfig from '../../../../Composables/useConfig.ts'
import BlockContent from '../UI/Content/BlockContent.vue'
import EntityLink from '../UI/Button/EntityLink.vue'
import Badge from '../UI/Badge.vue'
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import CancelSimulationConfirmationModal from './CancelSimulationConfirmationModal.vue'

export default {
  components: { CancelSimulationConfirmationModal, ButtonAll, ModalConfirmation, Badge, EntityLink, BlockContent },
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    simulation: { type: Object, required: true },
    def: { type: Object, required: true },
    updateDuration: { type: Boolean, default: false },
  },
  setup() {
    const { isTheme } = useConfig()

    return { isTheme }
  },
  data: () => ({
    cancelConfirmationDisplayed: false,
  }),
  computed: {
    isCancelable() {
      return !['CLIENT_CONTRACT', 'CANCELED'].includes(this.simulation.status)
    },
    sirenClientLabel() {
      if (this.simulation?.client?.countryCode === 'ES') {
        return this.$t('entity.nif.client')
      }

      return this.$t('simulation.fields.sirenClient.label')
    },
    sirenSupplierLabel() {
      if (this.simulation?.supplier?.countryCode === 'ES') {
        return this.$t('entity.nif.supplier')
      }

      return this.$t('simulation.fields.sirenSupplier.label')
    },
    paymentTermLabel() {
      if (
        this.simulation?.finance?.paymentTerm?.code &&
        this.$te('paymentTerm.choices.' + this.simulation.finance.paymentTerm.code)
      ) {
        return this.$t('paymentTerm.choices.' + this.simulation.finance.paymentTerm.code)
      } else if (this.simulation?.finance?.paymentTerm?.label && this.$te(this.simulation.finance.paymentTerm.label)) {
        return this.$t(this.simulation.finance.paymentTerm.label)
      }

      return ''
    },
    computeEquipmentAmount() {
      let amount = 0
      if (this.simulation?.finance?.simulationAmount) {
        amount = parseFloat(this.simulation.finance.simulationAmount)
        if (this.simulation.finance.outstanding && this.simulation.finance.computeType === 'RENT') {
          amount -= parseFloat(this.simulation.finance.outstanding)
        }
      }

      return this.$n(Number(amount), 'currency')
    },
    guaranteeDeposit() {
      if (this.simulation.finance.guaranteeDeposit === null) {
        return null
      }

      return Number(this.simulation.finance.guaranteeDeposit)
    },
  },
  methods: {
    sendReset(toCancel) {
      let url =
        window.location.protocol +
        '//' +
        window.location.host +
        '/devis/simulation.php?i=' +
        this.simulation.id +
        '&reset=true&index=creer'

      if (this.simulation.platformParam && this.simulation.platformParam.id) {
        url += '&pp=' + this.simulation.platformParam.id
      }

      if (toCancel) {
        url += '&cancel=true'
      }

      window.location = url
    },
    async cancel() {
      try {
        await this.deleteJSON('quotations/' + this.simulation.id, {})
        window.location = '/simulations/index.php?s=0'
      } catch ({ message }) {
        alert(message)
      } finally {
        this.cancelConfirmationDisplayed = false
      }
    },
    showSimulationResetModal() {
      this.$refs['confirmationReset'].showModal()
    },
    onReplaySimulation() {
      this.simulation.hasContract ? this.sendReset(false) : this.showSimulationResetModal()
    },
    showSimulationCancelModal() {
      this.cancelConfirmationDisplayed = true
    },
    hideSimulationCancelModal() {
      this.cancelConfirmationDisplayed = false
    },
  },
}
</script>
<template>
  <BlockContent e2e-block="simulationInformation">
    <div class="row d-flex">
      <div class="col col-md-4 flex-fill">
        <h4>{{ $t('contract.information.menu_params', { data: $t('contract.information.contributor') }) }}</h4>
        <div class="row mb-3">
          <div class="col">
            {{ $t('contract.information.client') }}
          </div>
          <div class="col">
            <EntityLink v-if="simulation.client" :entity="simulation.client" e2e-data="simulationClientName" />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ sirenClientLabel }}
          </div>
          <div class="col" e2e-data="simulationClientSiren">
            {{ simulation.client ? simulation.client.siren : '' }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('main.supplier') }}
          </div>
          <div class="col">
            <EntityLink v-if="simulation.supplier" :entity="simulation.supplier" e2e-data="simulationSupplierName" />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ sirenSupplierLabel }}
          </div>
          <div class="col" e2e-data="simulationSupplierSiren">
            {{ simulation.supplier?.siren ?? '' }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.platformSalesman.label') }}
          </div>
          <div class="col" e2e-data="simulationPlatformSalesmanName">
            {{ simulation.platformSalesman ? simulation.platformSalesman.name : '' }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.companySalesman.label') }}
          </div>
          <div class="col" e2e-data="simulationCompanySalesmanName">
            {{ simulation.companySalesman?.name ?? '' }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.createdBy.label') }}
          </div>
          <div class="col" e2e-data="simulationCreatedByName">
            {{ simulation.createdBy?.name ?? '' }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('demand.fields.createdBy.label') }}
          </div>
          <div class="col" e2e-data="simulationDemandCreatedByName">
            {{ simulation.decision?.demand?.createdBy?.name ?? '' }}
          </div>
        </div>
      </div>
      <div class="col col-md-4 flex-fill border-information">
        <h4>{{ $t('contract.information.menu_params', { data: $t('contract.list.table.equipment') }) }}</h4>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.equipmentType.label') }}
          </div>
          <div class="col" e2e-data="simulationEquipmentTypeName">
            {{ simulation.equipment?.equipmentType?.name ?? '' }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.productType.label') }}
          </div>
          <div class="col" e2e-data="simulationProductTypeName">
            {{ simulation.equipment?.productType?.name ?? '' }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.condition.label') }}
          </div>
          <div class="col" e2e-data="simulationConditionName">
            {{
              simulation?.equipment?.condition
                ? $t('equipmentCondition.choices.' + simulation.equipment.condition + '.label')
                : ''
            }}
          </div>
        </div>
        <div v-if="simulation.equipment?.year" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.year.label') }}
          </div>
          <div class="col" e2e-data="simulationEquipmentYear">
            {{ simulation.equipment.year }}
          </div>
        </div>
        <div v-if="simulation.finance?.outstanding" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.outstanding.label') }}
          </div>
          <div class="col" e2e-data="simulationOutstanding">
            {{ $n(Number(simulation.finance.outstanding ?? 0), 'currency') }}
          </div>
        </div>
        <div v-if="simulation.finance?.commissionAmount" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.commission.label') }}
          </div>
          <div class="col" e2e-data="simulationCommissionAmount">
            {{ $n(Number(simulation.finance.commissionAmount ?? 0), 'currency') }}
          </div>
        </div>
        <div v-if="simulation.finance?.outstanding && simulation.finance?.computeType === 'RENT'" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.equipmentAmount.label') }}
          </div>
          <div class="col" e2e-data="simulationEquipmentAmount">
            {{ computeEquipmentAmount }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">{{ $t('simulation.fields.amount.label') }}</div>
          <div class="col" e2e-data="simulationAmount">
            {{ $n(Number(simulation.finance?.amount ?? 0), 'currency') }}
          </div>
        </div>
        <div v-if="simulation.finance?.markup" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.markup.label') }}
          </div>
          <div class="col" e2e-data="simulationMarkup">
            {{ $n(Number(simulation.finance.markup ?? 0), 'currency') }}
          </div>
        </div>
        <div v-if="simulation.finance?.reversal" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.reversal.label') }}
          </div>
          <div class="col" e2e-data="simulationReversal">
            {{ $n(Number(simulation.finance.reversal ?? 0), 'currency') }}
          </div>
        </div>
      </div>
      <div class="col col-md-4 flex-fill">
        <h4>{{ $t('contract.information.menu_params', { data: $t('contract.information.prestation') }) }}</h4>
        <div v-if="simulation.finance?.contractMode" class="row mb-3">
          <div class="col">
            {{ def.finance.contractMode?.label ?? $t('simulation.fields.finance.contractMode.label') }}
          </div>
          <div class="col" e2e-data="simulationContractMode">
            {{ simulation.finance.contractMode.name }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.contractType.label') }}
          </div>
          <div class="col" e2e-data="simulationContractType">
            {{
              simulation.finance.contractType
                ? $t('simulation.fields.contractType.code.' + simulation.finance.contractType.code)
                : ''
            }}
          </div>
        </div>
        <div v-if="!isTheme('financia')" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.leaseback.label') }}
          </div>
          <div class="col" e2e-data="simulationLeaseback">
            {{ simulation.leaseback ? $t('main.bool.yes') : $t('main.bool.no') }}
          </div>
        </div>
        <div v-if="simulation.finance.residualPercent && !simulation.finance.residualAsLastRent" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.residualPercent.label') }}
          </div>
          <div class="col" e2e-data="simulationResidualPercent">{{ simulation.finance.residualPercent }} %</div>
        </div>
        <div v-else-if="simulation.finance.residualValue" class="row mb-3">
          <div class="col">
            {{
              simulation.finance.residualAsLastRent
                ? $t('simulation.fields.finance.residualAsLastRent.label')
                : $t('simulation.fields.residualValue.label')
            }}
          </div>
          <div class="col" e2e-data="simulationResidualValue">
            {{ $n(Number(simulation.finance.residualValue ?? 0), 'currency') }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.duration.label') }}
          </div>
          <div class="col" e2e-data="simulationDuration">
            {{
              updateDuration && (simulation.decision || simulation.finance)
                ? simulation.decision.rent.duration
                : simulation.finance.duration
            }}
            {{ $t('simulation.result.month') }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.period.label') }}
          </div>
          <div class="col" e2e-data="simulationPeriod">
            {{
              simulation.finance.period
                ? simulation.finance.period.label
                  ? $t(simulation.finance.period.label)
                  : simulation.finance.period.name
                : ''
            }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.paymentTerm.label') }}
          </div>
          <div class="col" e2e-data="simulationPaymentTermAndMean">
            {{ paymentTermLabel }} /
            {{
              simulation.finance.paymentMean
                ? $t('simulation.fields.paymentMean.code.' + simulation.finance.paymentMean.code)
                : ''
            }}
          </div>
        </div>
        <div v-if="simulation.finance?.coeffMargin?.margin && !isTheme('lbp')" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.coeffMargin.label') }}
          </div>
          <div class="col" e2e-data="simulationCoeffMargin">
            <template v-if="isTheme('olinn')">{{ simulation.finance.coeffMargin.name }}</template>
            <template v-else>{{ simulation.finance.coeffMargin.margin }} %</template>
          </div>
        </div>
        <div v-if="simulation.finance.initialRent && parseInt(simulation.finance.initialRent) != 0" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.initialRent.label') }}
          </div>
          <div class="col" e2e-data="simulationInitialRent">
            {{ $n(Number(simulation.finance.initialRent ?? 0), 'currency') }}
          </div>
        </div>
        <div v-if="simulation.finance.nbRentFree" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.nbRentFree.label') }}
          </div>
          <div class="col" e2e-data="simulationNbRentFree">
            {{ simulation.finance.nbRentFree }} {{ $t('simulation.result.month') }}
          </div>
        </div>
        <div v-if="guaranteeDeposit > 0" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.finance.guaranteeDeposit.label') }}
          </div>
          <div class="col" e2e-data="simulationGuaranteeDeposit">
            {{ $n(guaranteeDeposit, 'currency') }}
          </div>
        </div>
        <div v-if="simulation.formerContractNumber" class="row mb-3">
          <div class="col">
            {{ $t('contract.information.formerContractNumber') }}
          </div>
          <div class="col" e2e-data="simulationFormerContractNumber">
            {{ simulation.formerContractNumber }}
          </div>
        </div>
        <div v-if="simulation.frameworkContractNumber" class="row mb-3">
          <div class="col">
            {{ $t('contract.information.frameworkContractNumber') }}
          </div>
          <div class="col" e2e-data="simulationFrameworkContractNumber">
            {{ simulation.frameworkContractNumber }}
          </div>
        </div>
        <div v-if="simulation.finance.purchaseCategory" class="row mb-3">
          <div class="col">
            {{ def.finance.purchaseCategory?.label ?? $t('simulation.fields.purchaseCategory.label') }}
          </div>
          <div class="col" e2e-data="simulationPurchaseCategoryName">
            {{ simulation.finance.purchaseCategory.name }}
          </div>
        </div>
        <div v-if="simulation.finance.companyCategory" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.finance.companyCategory.label') }}
          </div>
          <div class="col" e2e-data="simulationCompanyCategoryName">
            {{ simulation.finance.companyCategory.name }}
          </div>
        </div>
        <div v-if="simulation.decision?.demand && isTheme('financia')" class="row mb-3">
          <div class="col">
            {{ $t('demand.fields.guarantees.label') }}
          </div>
          <div class="col" e2e-data="demandGuarantees">
            <template v-if="simulation?.decision?.demand?.guarantees?.length > 0">
              <Badge
                v-for="guarantee in simulation.decision.demand.guarantees"
                :key="guarantee.id"
                type="danger"
                class="mr-1"
              >
                {{ guarantee.name }}
              </Badge>
            </template>
          </div>
        </div>
        <div v-if="simulation.orderNumber" class="row mb-3">
          <div class="col">
            {{ $t('simulation.fields.orderNumber.label') }}
          </div>
          <div class="col" e2e-data="orderNumber">
            <template v-if="simulation.orderExternalUrl">
              <a :href="simulation.orderExternalUrl" class="font-weight-bold" target="_blank">
                {{ simulation.orderNumber }}
              </a>
            </template>
            <template v-else>
              {{ simulation.orderNumber }}
            </template>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!user.isReadOnly" class="row d-flex justify-content-center" style="margin: 1em auto">
      <ModalConfirmation
        id="reset"
        ref="confirmationReset"
        :message="'simulation.popin.action.reset'"
        button-confirm-title="main.bool.yes"
        button-cancel-title="main.bool.no"
        @confirm="sendReset"
        @cancel="sendReset"
      />
      <CancelSimulationConfirmationModal
        v-if="cancelConfirmationDisplayed"
        ref="confirmationCancel"
        @confirm="cancel"
        @cancel="hideSimulationCancelModal"
      />
      <ButtonAll
        size="large"
        label="simulation.input.button.computeReset"
        type="validation"
        e2e-button="redoSimulation"
        @click="onReplaySimulation"
      />
      <ButtonAll
        v-if="isCancelable"
        size="large"
        icon="cross"
        label="simulation.menu.cancel"
        type="validation"
        e2e-button="cancelSimulation"
        @click="showSimulationCancelModal"
      />
    </div>
  </BlockContent>
</template>
